/**
 * WordDictionary Service
 *
 * Validates words using Firebase Cloud Functions for security.
 * Caches validated words locally for performance and offline support.
 * This prevents exposing the complete dictionary to clients.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../config/firebase.js';

class WordDictionary {
  constructor(language = 'en') {
    this.language = language;
    this.validatedWords = new Map(); // Cache: word -> {valid: boolean, timestamp: number}
    this.isLoaded = true; // Always ready (no preloading needed)
    this.isLoading = false;
    this.loadPromise = null;
    this.errorCount = 0;
    this.functions = getFunctions(app);

    // Cache settings
    this.cacheExpiry = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    this.cacheVersion = 'v5'; // Increment to invalidate old caches (v5: added 19 Danish countries)
    this.cacheKey = `wordrive-validated-words-${this.cacheVersion}`;

    // Load cached words from localStorage
    this._loadCacheFromStorage();
  }

  /**
   * Load cached words from localStorage
   * @private
   */
  _loadCacheFromStorage() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (cached) {
        const data = JSON.parse(cached);
        const now = Date.now();

        // Filter out expired entries
        Object.entries(data).forEach(([word, entry]) => {
          if (now - entry.timestamp < this.cacheExpiry) {
            this.validatedWords.set(word, entry);
          }
        });

        console.log(`WordDictionary: Loaded ${this.validatedWords.size} cached words from storage`);
      }
    } catch (error) {
      console.warn('WordDictionary: Failed to load cache from storage:', error);
    }
  }

  /**
   * Save cache to localStorage
   * @private
   */
  _saveCacheToStorage() {
    try {
      const cacheObject = {};
      this.validatedWords.forEach((value, key) => {
        cacheObject[key] = value;
      });
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheObject));
    } catch (error) {
      console.warn('WordDictionary: Failed to save cache to storage:', error);
    }
  }

  /**
   * Initialize the dictionary (no-op for Cloud Function version)
   * @returns {Promise<boolean>} Always returns true
   */
  async initialize() {
    // No initialization needed - Cloud Function handles dictionary
    console.log('WordDictionary: Using Cloud Function validation (no preload needed)');
    this.isLoaded = true;
    return true;
  }

  /**
   * Validate if a word exists in the dictionary using Cloud Function
   * @param {string} word - The word to validate
   * @returns {Promise<boolean>} True if the word is valid, false otherwise
   */
  async validateWord(word) {
    if (!word || typeof word !== 'string') {
      console.warn('WordDictionary: Invalid word input:', word);
      return false;
    }

    // Normalize the word (uppercase, trim)
    const normalizedWord = word.trim().toUpperCase();

    // Check minimum length (typically 2+ letters for word games)
    if (normalizedWord.length < 2) {
      console.warn(`WordDictionary: Word too short: "${normalizedWord}"`);
      return false;
    }

    // Check cache first
    const cacheKey = `${this.language}:${normalizedWord}`;
    const cached = this.validatedWords.get(cacheKey);

    if (cached) {
      const age = Date.now() - cached.timestamp;
      if (age < this.cacheExpiry) {
        console.log(`✅ WordDictionary: Cache hit for "${normalizedWord}" (${this.language})`);
        return cached.valid;
      } else {
        // Remove expired entry
        this.validatedWords.delete(cacheKey);
      }
    }

    // Call Cloud Function for validation
    try {
      console.log(`🔍 WordDictionary: Validating "${normalizedWord}" with language="${this.language}"`);

      const validateWordFunction = httpsCallable(this.functions, 'validateWord');
      const result = await validateWordFunction({
        word: normalizedWord,
        language: this.language
      });

      const isValid = result.data.valid;

      console.log(`📥 WordDictionary: Cloud Function response:`, {
        word: result.data.word,
        valid: result.data.valid,
        language: result.data.language,
        requestedLanguage: this.language
      });

      // Cache the result
      this.validatedWords.set(cacheKey, {
        valid: isValid,
        timestamp: Date.now()
      });

      // Save cache to localStorage (throttled)
      this._saveCacheToStorage();

      console.log(`${isValid ? '✅' : '❌'} WordDictionary: "${normalizedWord}" (${this.language}) = ${isValid}`);
      return isValid;

    } catch (error) {
      console.error('❌ WordDictionary: Cloud Function error:', error);
      console.error('   Error details:', {
        message: error.message,
        code: error.code,
        details: error.details
      });

      // Fallback: if we have a cached result (even expired), use it
      if (cached) {
        console.warn('⚠️ WordDictionary: Using expired cache due to error');
        return cached.valid;
      }

      // Last resort: return false
      return false;
    }
  }

  /**
   * Batch validate multiple words (more efficient than individual calls)
   * @param {Array<string>} words - Array of words to validate
   * @returns {Promise<Array<{word: string, valid: boolean}>>}
   */
  async validateWords(words) {
    if (!Array.isArray(words) || words.length === 0) {
      return [];
    }

    // Check cache for all words first
    const results = [];
    const uncachedWords = [];

    for (const word of words) {
      const normalizedWord = word.trim().toUpperCase();
      const cacheKey = `${this.language}:${normalizedWord}`;
      const cached = this.validatedWords.get(cacheKey);

      if (cached && (Date.now() - cached.timestamp < this.cacheExpiry)) {
        results.push({ word: normalizedWord, valid: cached.valid });
      } else {
        uncachedWords.push(normalizedWord);
      }
    }

    // If all words were cached, return immediately
    if (uncachedWords.length === 0) {
      return results;
    }

    // Call Cloud Function for uncached words
    try {
      const validateWordsFunction = httpsCallable(this.functions, 'validateWords');
      const response = await validateWordsFunction({
        words: uncachedWords,
        language: this.language
      });

      // Cache the results
      response.data.results.forEach(result => {
        const cacheKey = `${this.language}:${result.word}`;
        this.validatedWords.set(cacheKey, {
          valid: result.valid,
          timestamp: Date.now()
        });
        results.push(result);
      });

      // Save cache
      this._saveCacheToStorage();

    } catch (error) {
      console.error('WordDictionary: Batch validation error:', error);
      // Return what we have from cache
    }

    return results;
  }

  /**
   * Get dictionary statistics
   * @returns {Object} Statistics about the cached words
   */
  getStats() {
    return {
      language: this.language,
      isLoaded: this.isLoaded,
      cachedWords: this.validatedWords.size,
      errorCount: this.errorCount
    };
  }

  /**
   * Check if dictionary is ready for use
   * @returns {boolean}
   */
  isReady() {
    return this.isLoaded;
  }

  /**
   * Get a sample of cached words for testing
   * @param {number} count - Number of sample words to return
   * @returns {Array<string>} Array of sample words
   */
  getSampleWords(count = 10) {
    const samples = [];
    let collected = 0;

    for (const [key] of this.validatedWords) {
      const word = key.split(':')[1]; // Remove language prefix
      samples.push(word);
      collected++;
      if (collected >= count) break;
    }

    return samples;
  }

  /**
   * Clear the validation cache
   */
  clearCache() {
    this.validatedWords.clear();
    try {
      localStorage.removeItem(this.cacheKey);
      console.log('WordDictionary: Cache cleared');
    } catch (error) {
      console.warn('WordDictionary: Failed to clear cache from storage:', error);
    }
  }

  /**
   * Change the dictionary language
   * @param {string} newLanguage - Language code (e.g., 'en', 'da')
   * @returns {Promise<boolean>} True if successfully changed
   */
  async changeLanguage(newLanguage) {
    if (newLanguage === this.language) {
      console.log(`🌍 WordDictionary: Already using language "${newLanguage}"`);
      return true; // Already using this language
    }

    console.log(`🌍 WordDictionary: Changing language from "${this.language}" to "${newLanguage}"`);

    // Update language
    const oldLanguage = this.language;
    this.language = newLanguage;

    console.log(`✅ WordDictionary: Language changed successfully`);
    console.log(`   Old language: "${oldLanguage}"`);
    console.log(`   New language: "${this.language}"`);
    console.log(`   Cache size: ${this.validatedWords.size} words`);

    // Cache is language-specific (keys include language prefix), so it's still valid
    // No need to clear or reload anything

    return true;
  }
}

// Create and export a singleton instance
const wordDictionary = new WordDictionary();

export default wordDictionary;
